import { motion } from "framer-motion";
import { Crown, Users, Headphones, Lock, ArrowRight } from "lucide-react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useIsMobile } from "@/hooks/use-mobile";

const AboutSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  
  const isMobile = useIsMobile();

  const features = [
    { icon: Lock, text: "100% Wyłączność", desc: "Cały lokal tylko dla Ciebie" },
    { icon: Headphones, text: "Pełna obsługa", desc: "Organizujemy wszystko od A do Z" },
    { icon: Users, text: "Eventy prywatne", desc: "18-stki, urodziny, imprezy" },
    { icon: Crown, text: "Eventy biznesowe", desc: "Konferencje, warsztaty, integracje" }
  ];

  const particles = reduceMotion ? [] : [...Array(3)].map((_, i) => ({
    id: i,
    left: `${20 + i * 25}%`,
    top: `${25 + i * 20}%`,
    delay: i * 1.5
  }));

  return (
    <section id="o-studio" ref={sectionRef} className="py-16 sm:py-24 md:py-32 lg:py-40 relative overflow-hidden bg-background">
      <div className="absolute top-0 left-0 right-0 h-64 md:h-80 pointer-events-none z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-background to-transparent" />
      </div>
      
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20" />
      
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[40px] pointer-events-none" />

      {!reduceMotion && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {particles.map(particle => (
            <div 
              key={particle.id} 
              className="absolute rounded-full bg-primary/30 w-2 h-2 animate-float-particle"
              style={{ left: particle.left, top: particle.top, animationDelay: `${particle.delay}s` }} 
            />
          ))}
        </div>
      )}

      <div className="absolute top-32 left-0 w-1/3 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <div className="absolute bottom-32 right-0 w-1/3 h-px bg-gradient-to-l from-transparent via-primary/20 to-transparent" />

      <div className="absolute top-20 left-8 md:left-16">
        <div className="w-20 h-px bg-gradient-to-r from-primary/30 to-transparent" />
        <div className="w-px h-20 bg-gradient-to-b from-primary/30 to-transparent" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.span 
            initial={isMobile ? false : { opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={isMobile ? { duration: 0 } : { duration: 0.5 }}
            className="text-primary font-bold text-sm uppercase tracking-[0.3em] mb-8 block"
          >
            Studio Skandal
          </motion.span>

          <motion.div 
            initial={isMobile ? false : { opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={isMobile ? { duration: 0 } : { duration: 0.6 }}
            className="relative mb-16"
          >
            <div className="absolute -inset-4 rounded-[2rem] border border-primary/10" />
            
            <div className="relative bg-gradient-to-br from-primary/15 via-primary/10 to-primary/5 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-14 overflow-hidden border border-primary/20">
              <div className="absolute -top-16 -right-16 w-40 h-40 bg-primary/10 rounded-full blur-[30px]" />
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-primary/8 rounded-full blur-[20px]" />
              
              <div className="absolute top-8 right-8 w-24 h-24 border border-primary/10 rounded-full" />
              
              <div className="relative z-10">
                <motion.h2 
                  initial={isMobile ? false : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={isMobile ? { duration: 0 } : { duration: 0.5, delay: 0.1 }}
                  className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-2 leading-[1.1]"
                >
                  Miejsce do robienia
                </motion.h2>
                <motion.div 
                  initial={isMobile ? false : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={isMobile ? { duration: 0 } : { duration: 0.5, delay: 0.15 }}
                  className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gradient-primary mb-4 sm:mb-6"
                >
                  rzeczy na poziomie
                </motion.div>
                <motion.p 
                  initial={isMobile ? false : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={isMobile ? { duration: 0 } : { duration: 0.5, delay: 0.2 }}
                  className="text-white/60 text-base sm:text-lg md:text-xl max-w-2xl font-light leading-relaxed mb-6 sm:mb-8"
                >
                  Nie wynajmujemy sali — <span className="font-semibold text-white/80">organizujemy eventy</span>. 
                  Wchodzisz, a wszystko działa. Dźwięk, światło, catering, obsługa. Ty się bawisz, my robimy resztę.
                </motion.p>
                <motion.div 
                  initial={isMobile ? false : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={isMobile ? { duration: 0 } : { duration: 0.5, delay: 0.25 }}
                >
                  <Link to="/o-miejscu">
                    <Button variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-primary/40 group">
                      Poznaj miejsce
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={isMobile ? false : { opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={isMobile ? { duration: 0 } : { duration: 0.5, delay: 0.2 }}
            className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6"
          >
            {features.map((feature, index) => (
              <motion.div 
                key={feature.text}
                initial={isMobile ? false : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={isMobile ? { duration: 0 } : { duration: 0.3, delay: 0.2 + index * 0.06 }}
                className="group relative touch-manipulation"
              >
                <div className="absolute inset-0 bg-primary/10 rounded-xl sm:rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="relative bg-white/3 backdrop-blur-sm border border-white/5 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 hover:border-primary/30 hover:bg-white/5 transition-all duration-300 hover:-translate-y-2 active:scale-[0.98]">
                  <div className="relative mb-3 sm:mb-4">
                    <feature.icon className="relative w-8 h-8 sm:w-10 sm:h-10 text-primary group-hover:text-primary-light transition-colors duration-300" />
                  </div>
                  <h3 className="text-white font-bold text-sm sm:text-base md:text-lg mb-1">
                    {feature.text}
                  </h3>
                  <p className="text-white/35 text-sm">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-48 md:h-64 pointer-events-none z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background" />
      </div>
    </section>
  );
};

export default AboutSection;
